import React, { useState, useEffect } from 'react';
import { Message, Button, Icon, Segment, Header, Table, Label, Progress } from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { 
  quizReportService, 
  TrainingMetricsDto, 
  ParticipantComparisonDto, 
  TeamComparisonDto, 
  TopPerformersDto, 
  TemporalAnalysisDto 
} from '../../services/learning/QuizReportService';
import { teamService, TeamListDto } from '../../services/teams/teamService';
import TrainingMetricsOverview from '../../components/Training/TrainingMetricsOverview';
import TeamComparisonChart from '../../components/Training/TeamComparisonChart';
import ParticipantRankingChart from '../../components/Training/ParticipantRankingChart';
import TemporalEvolutionChart from '../../components/Training/TemporalEvolutionChart';
import TeamPerformanceTable from '../../components/Training/TeamPerformanceTable';
import GlassmorphismTheme from '../../styles/glassmorphism';

const TrainingDashboardPage: React.FC = () => {
  const { hasRole } = useAuth();
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  
  // Estados dos dados
  const [trainingMetrics, setTrainingMetrics] = useState<TrainingMetricsDto | null>(null);
  const [participantsComparison, setParticipantsComparison] = useState<ParticipantComparisonDto[]>([]);
  const [teamsComparison, setTeamsComparison] = useState<TeamComparisonDto[]>([]);
  const [topPerformers, setTopPerformers] = useState<TopPerformersDto[]>([]);
  const [temporalAnalysis, setTemporalAnalysis] = useState<TemporalAnalysisDto | null>(null);
  const [trainingTeams, setTrainingTeams] = useState<TeamListDto[]>([]);
  
  // Estados dos filtros
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(undefined);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30);
  const [useCustomPeriod, setUseCustomPeriod] = useState<boolean>(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  useEffect(() => {
    if (!hasRole('Admin') && !hasRole('Supervisor')) {
      return;
    }

    loadTrainingTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRole]);

  // Carregar dados quando os filtros mudarem
  useEffect(() => {
    if (!hasRole('Admin') && !hasRole('Supervisor')) {
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRole, selectedTeamId, selectedPeriod, useCustomPeriod, customStartDate, customEndDate]);

  const loadTrainingTeams = async () => {
    try {
      const teams = await teamService.getTrainingTeams();
      setTrainingTeams(teams);
    } catch (err: any) {
      console.error('Erro ao carregar times de treinamento:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Calcular datas para análise temporal
      let startDate: Date;
      let endDate: Date = new Date();
      
      if (useCustomPeriod && customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
      } else {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - selectedPeriod);
      }

      // Carregar todos os dados em paralelo
      const [metrics, participants, teams, performers, temporal] = await Promise.all([
        quizReportService.getTrainingMetrics(),
        quizReportService.getParticipantsComparison(selectedTeamId),
        quizReportService.getTeamsComparison(
          useCustomPeriod && customStartDate ? customStartDate : undefined,
          useCustomPeriod && customEndDate ? customEndDate : undefined
        ),
        quizReportService.getTopPerformers(
          selectedTeamId, 
          undefined, 
          useCustomPeriod && customStartDate ? customStartDate : undefined,
          useCustomPeriod && customEndDate ? customEndDate : undefined,
          20
        ),
        quizReportService.getTemporalAnalysis(
          startDate.toISOString(),
          endDate.toISOString()
        )
      ]);

      setTrainingMetrics(metrics);
      setParticipantsComparison(participants);
      setTeamsComparison(teams);
      setTopPerformers(performers);
      setTemporalAnalysis(temporal);
    } catch (err: any) {
      showError(err.message || 'Erro ao carregar dados do dashboard de treinamento');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period: number) => {
    setSelectedPeriod(period);
    setUseCustomPeriod(false);
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const handleApplyCustomPeriod = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start > end) {
        showError('Data de início deve ser anterior à data de fim');
        return;
      }
      setUseCustomPeriod(true);
    } else {
      showError('Por favor, selecione ambas as datas');
    }
  };

  if (!hasRole('Admin') && !hasRole('Supervisor')) {
    return (
      <GlassPageWrapper title="Acesso Negado" icon="lock">
        <Message negative>
          <Message.Header>Acesso Restrito</Message.Header>
          <p>Você não tem permissão para acessar esta página.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  const periodOptions = [
    { key: 7, text: '7 Dias', value: 7 },
    { key: 30, text: '30 Dias', value: 30 },
    { key: 90, text: '90 Dias', value: 90 },
  ];

  return (
    <GlassPageWrapper title="Dashboard de Treinamento" icon="graduation cap">
      <div style={{ padding: GlassmorphismTheme.spacing.md }}>
        {/* Filtros */}
        <div style={{
          ...GlassmorphismTheme.components.glassCard,
          padding: GlassmorphismTheme.spacing.md,
          marginBottom: GlassmorphismTheme.spacing.lg,
          display: 'flex',
          gap: GlassmorphismTheme.spacing.md,
          flexWrap: 'wrap',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ flex: 1, minWidth: '200px', width: '100%' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              color: GlassmorphismTheme.colors.solid.textLight,
              fontWeight: 500
            }}>
              Período
            </label>
            {/* Primeira linha: Botões de período */}
            <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', alignItems: 'center', marginBottom: useCustomPeriod ? '12px' : '0' }}>
              {periodOptions.map(option => (
                <Button
                  key={option.key}
                  onClick={() => handlePeriodChange(option.value as number)}
                  active={!useCustomPeriod && selectedPeriod === option.value}
                  style={{
                    ...(!useCustomPeriod && selectedPeriod === option.value
                      ? GlassmorphismTheme.components.buttonPrimary 
                      : GlassmorphismTheme.components.buttonGlass)
                  }}
                >
                  {option.text}
                </Button>
              ))}
              <Button
                onClick={() => {
                  setUseCustomPeriod(!useCustomPeriod);
                  if (!useCustomPeriod) {
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }
                }}
                active={useCustomPeriod}
                style={{
                  ...(useCustomPeriod 
                    ? GlassmorphismTheme.components.buttonPrimary 
                    : GlassmorphismTheme.components.buttonGlass),
                  marginLeft: '4px'
                }}
              >
                {useCustomPeriod ? '✓ Personalizado' : 'Personalizado'}
              </Button>
            </div>
            {/* Segunda linha: Campos de data personalizada */}
            {useCustomPeriod && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontSize: '0.9rem',
                    minWidth: '150px'
                  }}
                  placeholder="Data Inicial"
                />
                <span style={{ color: GlassmorphismTheme.colors.solid.textLight, margin: '0 4px' }}>até</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontSize: '0.9rem',
                    minWidth: '150px'
                  }}
                  placeholder="Data Final"
                />
                <Button
                  onClick={handleApplyCustomPeriod}
                  disabled={!customStartDate || !customEndDate}
                  loading={loading}
                  icon="calendar check"
                  style={{
                    ...GlassmorphismTheme.components.buttonPrimary,
                    opacity: (!customStartDate || !customEndDate) ? 0.5 : 1,
                    cursor: (!customStartDate || !customEndDate) ? 'not-allowed' : 'pointer',
                    marginLeft: '8px'
                  }}
                >
                  Aplicar
                </Button>
              </div>
            )}
            {/* Botão Filtrar quando não está em período personalizado */}
            {!useCustomPeriod && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px' }}>
                <Button
                  onClick={loadData}
                  disabled={loading}
                  loading={loading}
                  icon="filter"
                  style={{
                    ...GlassmorphismTheme.components.buttonPrimary
                  }}
                >
                  Filtrar
                </Button>
              </div>
            )}
          </div>

          {trainingTeams.length > 0 && (
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                color: GlassmorphismTheme.colors.solid.textLight,
                fontWeight: 500
              }}>
                Filtrar por Turma
              </label>
              <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                <Button
                  onClick={() => setSelectedTeamId(undefined)}
                  active={selectedTeamId === undefined}
                  style={{
                    ...(selectedTeamId === undefined 
                      ? GlassmorphismTheme.components.buttonPrimary 
                      : GlassmorphismTheme.components.buttonGlass)
                  }}
                >
                  Todas
                </Button>
                {trainingTeams.map(team => (
                  <Button
                    key={team.id}
                    onClick={() => setSelectedTeamId(team.id)}
                    active={selectedTeamId === team.id}
                    style={{
                      ...(selectedTeamId === team.id 
                        ? GlassmorphismTheme.components.buttonPrimary 
                        : GlassmorphismTheme.components.buttonGlass)
                    }}
                  >
                    {team.name.length > 20 ? team.name.substring(0, 20) + '...' : team.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Métricas Principais */}
        <div style={{ marginBottom: GlassmorphismTheme.spacing.lg }}>
          <TrainingMetricsOverview metrics={trainingMetrics} />
        </div>

        {/* Gráfico de Comparação de Turmas */}
        {teamsComparison.length > 0 && (
          <div style={{ marginBottom: GlassmorphismTheme.spacing.lg }}>
            <TeamComparisonChart 
              data={teamsComparison}
              title="Comparação de Turmas"
              subtitle="Comparação visual entre diferentes turmas de treinamento"
            />
          </div>
        )}

        {/* Tabela de Performance por Turma */}
        <div style={{ marginBottom: GlassmorphismTheme.spacing.lg }}>
          <TeamPerformanceTable 
            data={teamsComparison}
            title="Performance Detalhada por Turma"
            subtitle="Análise completa de cada turma de treinamento"
          />
        </div>

        {/* Ranking de Participantes */}
        {topPerformers.length > 0 && (
          <>
            <div style={{ marginBottom: GlassmorphismTheme.spacing.lg }}>
              <ParticipantRankingChart 
                data={topPerformers}
                title="Top 10 Participantes"
                subtitle="Ranking dos melhores performers"
                limit={10}
              />
            </div>

            {/* Tabela de Participantes */}
            <Segment style={{ 
              ...GlassmorphismTheme.components.glassCard,
              marginBottom: GlassmorphismTheme.spacing.lg,
              background: '#ffffff'
            }}>
              <Header as="h3" style={{ marginBottom: '16px' }}>
                <Icon name="users" />
                Comparação de Participantes
              </Header>
              {participantsComparison.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <Table celled striped>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Rank</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Turma</Table.HeaderCell>
                        <Table.HeaderCell>Média</Table.HeaderCell>
                        <Table.HeaderCell>Taxa de Aprovação</Table.HeaderCell>
                        <Table.HeaderCell>Aprovados</Table.HeaderCell>
                        <Table.HeaderCell>Reprovados</Table.HeaderCell>
                        <Table.HeaderCell>Total de Tentativas</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {participantsComparison.map((participant) => (
                        <Table.Row key={participant.userId}>
                          <Table.Cell>
                            <Label color={participant.rank <= 3 ? 'yellow' : undefined}>
                              {participant.rank}º
                            </Label>
                          </Table.Cell>
                          <Table.Cell>{participant.userName}</Table.Cell>
                          <Table.Cell>{participant.teamName || 'N/A'}</Table.Cell>
                          <Table.Cell>
                            <strong>{participant.averageScore?.toFixed(1) || 'N/A'}%</strong>
                          </Table.Cell>
                          <Table.Cell>
                            <Progress
                              percent={participant.approvalRate}
                              color={participant.approvalRate >= 70 ? 'green' : participant.approvalRate >= 50 ? 'yellow' : 'red'}
                              size="small"
                            >
                              {participant.approvalRate.toFixed(1)}%
                            </Progress>
                          </Table.Cell>
                          <Table.Cell>
                            <Label color="green">{participant.passedAttempts}</Label>
                          </Table.Cell>
                          <Table.Cell>
                            <Label color="red">{participant.failedAttempts}</Label>
                          </Table.Cell>
                          <Table.Cell>{participant.totalAttempts}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              ) : (
                <Message info>
                  <Message.Header>Nenhum dado disponível</Message.Header>
                  <p>Não há dados de participantes para exibir no momento.</p>
                </Message>
              )}
            </Segment>
          </>
        )}

        {/* Análise Temporal */}
        <div style={{ marginBottom: GlassmorphismTheme.spacing.lg }}>
          <TemporalEvolutionChart 
            data={temporalAnalysis}
            title="Evolução Temporal"
            subtitle={`Evolução das métricas ao longo do tempo (${useCustomPeriod && customStartDate && customEndDate 
              ? `${new Date(customStartDate).toLocaleDateString('pt-BR')} - ${new Date(customEndDate).toLocaleDateString('pt-BR')}`
              : `Últimos ${selectedPeriod} dias`})`}
          />
        </div>
      </div>
    </GlassPageWrapper>
  );
};

export default TrainingDashboardPage;

